import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog, Bar, Title, Button, BusyIndicator, MessageStrip, FlexBox,
  SegmentedButton, SegmentedButtonItem, Input, List, ListItemStandard
} from '@ui5/webcomponents-react';
import { resolveRokuIp } from './rokuDiscovery';
import {
  sendKey, sendText, launchApp,
  queryActiveApp, queryMediaPlayer, queryApps,
  isLimitedModeError
} from './rokuEcp';

const POLL_MS = 15000;

function isIOS() {
  return window.Capacitor && typeof window.Capacitor.getPlatform === 'function'
    && window.Capacitor.getPlatform() === 'ios';
}

function RokuRemote({ open, onClose, device }) {
  const serial = device && device.device_id;
  const displayName = (device && (device.display_name || device.account_type)) || 'Roku Remote';

  const [ip, setIp] = useState('');
  const [discovering, setDiscovering] = useState(false);
  const [discoveryError, setDiscoveryError] = useState('');
  const [limitedMode, setLimitedMode] = useState(false);

  const [activeApp, setActiveApp] = useState('');
  const [playerState, setPlayerState] = useState('');
  const [contentTitle, setContentTitle] = useState('');
  const [position, setPosition] = useState('');
  const [duration, setDuration] = useState('');

  const [apps, setApps] = useState([]);
  const [typedText, setTypedText] = useState('');
  const [sending, setSending] = useState(false);
  const [activeView, setActiveView] = useState('remote'); // 'remote' | 'apps'

  const pollTimer = useRef(null);
  const stoppedRef = useRef(false);

  // Discovery on open
  useEffect(() => {
    if (!open || !serial) return undefined;
    stoppedRef.current = false;
    setIp('');
    setDiscoveryError('');
    setLimitedMode(false);
    setDiscovering(true);

    resolveRokuIp(serial)
      .then(resolved => {
        if (stoppedRef.current) return;
        if (!resolved) {
          setDiscoveryError(
            'No Roku devices were found on this Wi-Fi network.\n\n' +
            '\u2022 Make sure you are on the same Wi-Fi as the Roku\n' +
            '\u2022 Roku TVs must be powered on, or have "Fast TV Start" enabled\n' +
            (isIOS() ? '\u2022 On iOS, enable Settings \u2192 Home Alerts \u2192 Local Network' : '')
          );
        } else {
          setIp(resolved);
        }
      })
      .catch(e => {
        if (stoppedRef.current) return;
        const raw = (e && e.message) ? e.message : String(e || '');
        // "create error" is specifically the Android UdpSocket plugin failing
        // at socket construction — match exactly, not any word containing
        // "create", so downstream errors (joinGroup, send) surface verbatim.
        const isCreateErr = raw.trim().toLowerCase() === 'create error'
          || raw.toLowerCase().startsWith('create error');
        const friendly = isCreateErr
          ? 'Couldn\u2019t start Roku discovery on this device. Make sure Wi-Fi is on and you\u2019re connected to the same network as the Roku, then try again.'
          : (raw || 'Discovery failed');
        setDiscoveryError(friendly);
      })
      .finally(() => {
        if (!stoppedRef.current) setDiscovering(false);
      });

    return () => {
      stoppedRef.current = true;
      if (pollTimer.current) { clearTimeout(pollTimer.current); pollTimer.current = null; }
    };
  }, [open, serial]);

  // Polling active-app + media-player
  const pollState = useCallback(async () => {
    if (!ip || stoppedRef.current) return;
    try {
      const a = await queryActiveApp(ip);
      if (stoppedRef.current) return;
      setActiveApp(a.activeApp);
    } catch (e) {
      if (isLimitedModeError(e)) setLimitedMode(true);
    }
    if (!limitedMode) {
      try {
        const m = await queryMediaPlayer(ip);
        if (stoppedRef.current) return;
        setPlayerState(m.playerState || '');
        setContentTitle(m.contentTitle || '');
        setPosition(m.position || '');
        setDuration(m.duration || '');
      } catch (e) {
        if (isLimitedModeError(e)) setLimitedMode(true);
      }
    }
  }, [ip, limitedMode]);

  useEffect(() => {
    if (!ip) return undefined;
    let cancelled = false;
    const loop = () => {
      if (cancelled || stoppedRef.current) return;
      pollState().finally(() => {
        if (!cancelled && !stoppedRef.current) {
          pollTimer.current = setTimeout(loop, POLL_MS);
        }
      });
    };
    loop();
    // Load apps + device-info once
    queryApps(ip).then(list => { if (!cancelled) setApps(list); })
      .catch(e => { if (isLimitedModeError(e)) setLimitedMode(true); });

    return () => {
      cancelled = true;
      if (pollTimer.current) { clearTimeout(pollTimer.current); pollTimer.current = null; }
    };
  }, [ip, pollState]);

  const press = async (key) => {
    if (!ip) return;
    try {
      await sendKey(ip, key);
    } catch (e) {
      if (isLimitedModeError(e)) setLimitedMode(true);
    }
  };

  const onSendText = async () => {
    const t = (typedText || '').trim();
    if (!t || !ip) return;
    setSending(true);
    try {
      await sendText(ip, t);
      setTypedText('');
    } catch (e) {
      if (isLimitedModeError(e)) setLimitedMode(true);
    } finally {
      setSending(false);
    }
  };

  const onLaunch = async (appId) => {
    if (!ip || !appId) return;
    try { await launchApp(ip, appId); } catch (e) {}
  };

  if (!open) return null;

  const header = (
    <Bar
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      startContent={<Title>{displayName}</Title>}
      endContent={<Button design="Transparent" icon="decline" onClick={onClose} />}
    />
  );

  return (
    <Dialog open={open} onAfterClose={onClose} header={header} stretch style={{ width: '100vw', height: '100vh' }}>
      <div style={{ padding: '0.5rem', paddingBottom: 'calc(0.5rem + env(safe-area-inset-bottom, 0px))' }}>

        {discovering && (
          <FlexBox direction="Column" alignItems="Center" style={{ padding: '2rem' }}>
            <BusyIndicator active size="Medium" />
            <div style={{ marginTop: '0.75rem', color: 'var(--sapContent_LabelColor)' }}>
              Scanning Wi-Fi for Roku…
            </div>
          </FlexBox>
        )}

        {!discovering && discoveryError && (
          <MessageStrip design="Negative" hideCloseButton style={{ whiteSpace: 'pre-line' }}>
            {discoveryError}
          </MessageStrip>
        )}

        {!discovering && ip && (
          <>
            {/* Compact status line: app · state · position */}
            <div style={{
              padding: '0.25rem 0.5rem',
              fontSize: '0.8125rem',
              color: 'var(--sapContent_LabelColor)',
              textAlign: 'center',
              borderBottom: '1px solid var(--sapGroup_TitleBorderColor, #e5e5e5)'
            }}>
              {[activeApp, playerState, position && `${position}/${duration}`]
                .filter(Boolean).join(' · ') || '—'}
            </div>

            {limitedMode && (
              <MessageStrip design="Critical" hideCloseButton>
                Roku Limited Mode is enabled. Remote control commands are blocked. To allow ECP access, go to Roku Settings → System → Advanced and disable Limited Mode.
              </MessageStrip>
            )}

            <div style={{ padding: '0.5rem', display: 'flex', justifyContent: 'center' }}>
              <SegmentedButton
                selectionMode="Single"
                onSelectionChange={e => {
                  const sel = e.detail.selectedItems && e.detail.selectedItems[0];
                  if (sel) setActiveView(sel.dataset.view);
                }}
              >
                <SegmentedButtonItem data-view="remote" selected={activeView === 'remote'}>Remote</SegmentedButtonItem>
                <SegmentedButtonItem data-view="apps"   selected={activeView === 'apps'}>Apps</SegmentedButtonItem>
              </SegmentedButton>
            </div>

            {activeView === 'remote' && (
              <div style={{ padding: '0.75rem' }}>
                  {/* Power */}
                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Power</div>
                    <FlexBox justifyContent="Center" gap="0.5rem">
                      <Button design="Positive" icon="activate" onClick={() => press('PowerOn')}>Power On</Button>
                      <Button design="Negative" icon="decline" onClick={() => press('PowerOff')}>Power Off</Button>
                    </FlexBox>
                  </div>

                  {/* D-Pad */}
                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Navigation</div>
                    <FlexBox direction="Column" alignItems="Center" gap="0.375rem">
                      <Button icon="slim-arrow-up" onClick={() => press('Up')} style={{ width: '4rem' }} />
                      <FlexBox gap="0.375rem" alignItems="Center">
                        <Button icon="slim-arrow-left" onClick={() => press('Left')} style={{ width: '4rem' }} />
                        <Button design="Emphasized" onClick={() => press('Select')} style={{ width: '4rem' }}>OK</Button>
                        <Button icon="slim-arrow-right" onClick={() => press('Right')} style={{ width: '4rem' }} />
                      </FlexBox>
                      <Button icon="slim-arrow-down" onClick={() => press('Down')} style={{ width: '4rem' }} />
                      <FlexBox gap="0.5rem" style={{ marginTop: '0.5rem' }}>
                        <Button icon="nav-back" onClick={() => press('Back')}>Back</Button>
                        <Button icon="home" onClick={() => press('Home')}>Home</Button>
                      </FlexBox>
                    </FlexBox>
                  </div>

                  {/* Media */}
                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Media</div>
                    <FlexBox justifyContent="Center" gap="0.375rem">
                      <Button icon="media-rewind" onClick={() => press('Rev')} />
                      <Button
                        design="Emphasized"
                        icon={playerState === 'Playing' ? 'media-pause' : 'media-play'}
                        onClick={() => press('Play')}
                      />
                      <Button icon="media-forward" onClick={() => press('Fwd')} />
                      <Button icon="restart" onClick={() => press('InstantReplay')} />
                      <Button icon="action-settings" onClick={() => press('Info')} tooltip="Options" />
                    </FlexBox>
                  </div>

                  {/* Volume */}
                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Volume</div>
                    <FlexBox justifyContent="Center" gap="0.375rem">
                      <Button icon="sound" onClick={() => press('VolumeDown')} />
                      <Button icon="sound-off" onClick={() => press('VolumeMute')} />
                      <Button icon="sound-loud" onClick={() => press('VolumeUp')} />
                    </FlexBox>
                  </div>

                  {/* Keyboard */}
                  <div>
                    <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Keyboard</div>
                    <FlexBox alignItems="Center" gap="0.375rem">
                      <Input
                        value={typedText}
                        placeholder="Type text to send…"
                        onInput={e => setTypedText(e.target.value)}
                        style={{ flex: 1 }}
                      />
                      <Button
                        design="Emphasized"
                        icon="paper-plane"
                        onClick={onSendText}
                        disabled={sending || !typedText.trim()}
                      >
                        Send
                      </Button>
                    </FlexBox>
                  </div>
                </div>
            )}

            {activeView === 'apps' && (
              <div style={{ padding: '0.5rem' }}>
                {limitedMode ? (
                  <div style={{ padding: '1rem', color: 'var(--sapContent_LabelColor)' }}>
                    App list unavailable (Roku Limited Mode is enabled).
                  </div>
                ) : apps.length === 0 ? (
                  <FlexBox justifyContent="Center" style={{ padding: '1rem' }}>
                    <BusyIndicator active size="Small" />
                  </FlexBox>
                ) : (
                  <List headerText="Installed Channels">
                    {apps.map(app => (
                      <ListItemStandard
                        key={app.id}
                        type="Active"
                        onClick={() => onLaunch(app.id)}
                      >
                        {app.name}
                      </ListItemStandard>
                    ))}
                  </List>
                )}
              </div>
            )}
          </>
        )}

      </div>
    </Dialog>
  );
}

export default RokuRemote;
